import React, { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../Config.js';
import styles from './SchoolSalesReport.module.css';
import Select from "react-select";
import { exportToExcel } from '../Export/exportToExcel.js';
import {
    CalendarRange,
    Download,
    Eye,
    FileSearch,
    GraduationCap,
    IndianRupee,
    School,
    Search,
    ShoppingBag,
    TriangleAlert,
    UserRound,
    X,
} from 'lucide-react';

// Amount with Indian digit grouping, for display only (Excel exports keep raw numbers)
const formatAmount = (value) =>
    Number(value || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// yyyy-MM-dd -> dd-MM-yyyy
const formatDate = (value) => (value ? value.split('-').reverse().join('-') : '');

// react-select styled like the View Stock controls (36px tall, 8px radius, blue focus ring).
// Menus render into document.body so the results card doesn't clip them.
const selectStyles = {
    control: (base, state) => ({
        ...base,
        minHeight: 36,
        height: 36,
        borderRadius: 8,
        borderColor: state.isFocused ? '#2563eb' : '#e2e8f0',
        boxShadow: state.isFocused ? '0 0 0 3px rgba(37, 99, 235, 0.15)' : 'none',
        fontSize: 13,
        '&:hover': { borderColor: state.isFocused ? '#2563eb' : '#cbd5e1' },
    }),
    valueContainer: (base) => ({ ...base, padding: '0 8px' }),
    input: (base) => ({ ...base, margin: 0, padding: 0 }),
    indicatorsContainer: (base) => ({ ...base, height: 34 }),
    indicatorSeparator: () => ({ display: 'none' }),
    placeholder: (base) => ({ ...base, color: '#94a3b8' }),
    singleValue: (base) => ({ ...base, color: '#0f172a' }),
    menu: (base) => ({ ...base, fontSize: 13, borderRadius: 8, overflow: 'hidden' }),
    menuPortal: (base) => ({ ...base, zIndex: 1100 }),
    option: (base, state) => ({
        ...base,
        backgroundColor: state.isSelected ? '#2563eb' : state.isFocused ? '#eff6ff' : '#ffffff',
        color: state.isSelected ? '#ffffff' : '#0f172a',
    }),
};

const SalesSchoolReport = () => {
    const user = JSON.parse(sessionStorage.getItem('user'));
    const storeId = user?.storeId;

    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [reportData, setReportData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [popupData, setPopupData] = useState(null);
    const [popupVisible, setPopupVisible] = useState(false);
    const [userList, setUserList] = useState([]);
    const [userId, setUserId] = useState('ALL');
    const [allSchools, setAllSchools] = useState([]);
    const [selectedSchool, setSelectedSchool] = useState('');
    const selectedSchoolRef = useRef(null);
    // UI only: tells "not fetched yet" apart from "fetched, nothing found", and titles the detail popup
    const [hasFetched, setHasFetched] = useState(false);
    const [detailSchool, setDetailSchool] = useState('');

    // Headers for Export - Detailed Report
    const popupHeaders = [
        { label: "Item Barcode ID", key: "itemBarcodeID" },
        { label: "Item Category", key: "itemCategory" },
        { label: "Item Code", key: "itemCode" },
        { label: "Description", key: "description" },
        { label: "Type", key: "itemType" },
        { label: "Color", key: "itemColor" },
        { label: "Size", key: "itemSize" },
        { label: "Price", key: "price" },
        { label: "Sell Price", key: "sellPrice" },
        { label: "Quantity", key: "totalQuantity" },
        { label: "Total Amount", key: "totalAmount" },
        { label: "Bill Type", key: "billType" }
    ];

    // Headers for Export - Master Table
    const masterHeaders = [
        { label: "School", key: "schoolName" },
        { label: "School Sales", key: "schoolSales" },
        { label: "Other Sales", key: "otherSales" },
        { label: "Total Sales", key: "sales" }
    ];

    // Dynamic filename for detailed report
    const getExportFilename = () => {
        let school = selectedSchool || "School Sales Report";

        if (!startDate || !endDate) {
            return school;
        }
        return `${school}_${startDate}_to_${endDate}`;
    };

    // Dynamic filename for master table
    const getMasterExportFilename = () => {
        if (!startDate || !endDate) {
            return "School_Sales_Summary";
        }
        return `School_Sales_Summary_${startDate}_to_${endDate}`;
    };

    // Handle Excel Export for detailed popup
    const handleExportToExcel = () => {
        if (popupData && popupData.length > 0) {
            exportToExcel(popupHeaders, popupData, getExportFilename());
        }
    };

    // Handle Excel Export for master table
    const handleExportMasterToExcel = () => {
        if (reportData && reportData.length > 0) {
            exportToExcel(masterHeaders, reportData, getMasterExportFilename());
        } else {
            alert('No data available to export');
        }
    };

    const fetchAllSchools = async () => {
        try {
            const user = JSON.parse(sessionStorage.getItem('user'));
            const storeId = user?.storeId;
            const response = await axios.get(`${API_BASE_URL}/user/filter/getSchoolNameandCode`, {
                params: { storeId }
            });

            if (Array.isArray(response.data)) {
                const schoolOptions = response.data.map((school) => ({
                    value: `${school.schoolName} (${school.schoolCode})`,
                    label: `${school.schoolName} (${school.schoolCode})`,
                    schoolName: school.schoolName,
                    schoolCode: school.schoolCode,
                }));
                setAllSchools(schoolOptions);
            }
        } catch (error) {
            console.error('Error fetching schools:', error);
        }
    };

    useEffect(() => {
        fetchAllSchools();
    }, []);

    const fetchSalesReport = async () => {
        try {
            setLoading(true);
            setError(null);

            let apiUrl;

            if (selectedSchool != null && selectedSchool.trim() !== '') {
                apiUrl = `${API_BASE_URL}/sales/report/date/schoolList?startDate=${startDate}&endDate=${endDate}&school=${encodeURIComponent(selectedSchool.trim())}&userId=${userId}&storeId=${storeId}`;
            } else {
                apiUrl = `${API_BASE_URL}/sales/report/school_name?startDate=${startDate}&endDate=${endDate}&storeId=${storeId}&userId=${userId}`;
            }

            const response = await fetch(apiUrl);

            if (!response.ok) {
                throw new Error('Failed to fetch data');
            }

            const data = await response.json();
            const sortedData = data.sort((a, b) => b.sales - a.sales);
            setReportData(sortedData);
        } catch (err) {
            console.log(err.message);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        const today = new Date();
        const formatted = today.toISOString().split('T')[0];
        setEndDate(formatted);
    }, []);

    const fetchDetailedReport = async (schoolName) => {
        try {
            setLoading(true);
            const response = await fetch(`${API_BASE_URL}/sales/report/schoolSalesSummary?schoolName=${schoolName}&startDate=${startDate}&endDate=${endDate}&storeId=${storeId}`);
            if (!response.ok) throw new Error('Failed to fetch detailed report');

            const data = await response.json();
            setPopupData(data);
            setPopupVisible(true);
        } catch (err) {
            console.log(err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleFetch = () => {
        if (startDate && endDate && storeId) {
            setHasFetched(true);
            fetchSalesReport();
        } else {
            alert('Please select start and end dates');
        }
    };

    const handleSelectChange = (selectedOption) => {
        setSelectedSchool(selectedOption ? selectedOption.schoolName : '');
    };

    const handleClearSchool = () => setSelectedSchool('');

    // Refetch whenever the school is picked or cleared. Runs after the state update,
    // so fetchSalesReport sees the new school. Skipped until both dates are set.
    useEffect(() => {
        if (startDate && endDate && storeId) {
            setHasFetched(true);
            fetchSalesReport();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedSchool]);

    useEffect(() => {
        const fetchUser = async () => {
            try {
                const response = await fetch(`${API_BASE_URL}/store/getAllUserByStore?storeId=${storeId}`);
                const data = await response.json();
                const options = [{ value: "ALL", label: "ALL" }, ...data.map(u => ({ value: u, label: u }))];
                setUserList(options);
            } catch (error) {
                console.error("Error fetching users:", error);
            }
        };

        fetchUser();
    }, [API_BASE_URL, storeId]);

    const handleView = (schoolName) => {
        setDetailSchool(schoolName);
        fetchDetailedReport(schoolName);
    };

    const closePopup = () => {
        setPopupVisible(false);
        setPopupData(null);
    };

    // Close the detail popup on Escape
    useEffect(() => {
        if (!popupVisible) return undefined;

        const handleKeyDown = (event) => {
            if (event.key === 'Escape') {
                setPopupVisible(false);
                setPopupData(null);
            }
        };

        document.addEventListener('keydown', handleKeyDown);
        return () => {
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [popupVisible]);

    const hasData = reportData.length > 0;
    const sumBy = (rows, key) => rows.reduce((total, row) => total + (Number(row[key]) || 0), 0);
    const schoolSalesTotal = sumBy(reportData, 'schoolSales');
    const otherSalesTotal = sumBy(reportData, 'otherSales');
    const totalSales = sumBy(reportData, 'sales');

    const summaryCards = [
        { key: 'total', label: 'Total sales', value: `₹${formatAmount(totalSales)}`, icon: IndianRupee, primary: true },
        { key: 'school', label: 'School sales', value: `₹${formatAmount(schoolSalesTotal)}`, icon: GraduationCap },
        { key: 'other', label: 'Other sales', value: `₹${formatAmount(otherSalesTotal)}`, icon: ShoppingBag },
        { key: 'count', label: 'Schools', value: reportData.length, icon: School },
    ];

    return (
        <>
            {/* Detail popup — rendered outside the page so it covers the whole dashboard */}
            {popupVisible && popupData && (
                <div className={styles.modalOverlay} onClick={closePopup}>
                    <div
                        className={styles.modal}
                        role="dialog"
                        aria-modal="true"
                        aria-labelledby="school-sales-detail-title"
                        onClick={(event) => event.stopPropagation()}
                    >
                        <div className={styles.modalHeader}>
                            <div>
                                <h3 id="school-sales-detail-title" className={styles.modalTitle}>
                                    Sales report details
                                </h3>
                                <p className={styles.modalSubtitle}>
                                    {detailSchool}
                                    {startDate && endDate && ` · ${formatDate(startDate)} → ${formatDate(endDate)}`}
                                </p>
                            </div>
                            <button type="button" className={styles.iconBtn} onClick={closePopup} aria-label="Close">
                                <X size={18} aria-hidden="true" />
                            </button>
                        </div>

                        <div className={styles.modalBody}>
                            <div className={styles.tableWrap}>
                                {popupData.length > 0 ? (
                                    <table className={styles.table}>
                                        <thead>
                                            <tr>
                                                <th>Item Barcode ID</th>
                                                <th>Item Category</th>
                                                <th>Item Code</th>
                                                <th>Description</th>
                                                <th>Type</th>
                                                <th>Color</th>
                                                <th>Size</th>
                                                <th className={styles.num}>Price</th>
                                                <th className={styles.num}>Sell Price</th>
                                                <th className={styles.num}>Quantity</th>
                                                <th className={styles.num}>Total Amount</th>
                                                <th>Bill Type</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {popupData.map((item, index) => (
                                                <tr key={index}>
                                                    <td>{item.itemBarcodeID}</td>
                                                    <td>{item.itemCategory}</td>
                                                    <td className={styles.strong}>{item.itemCode}</td>
                                                    <td>{item.description}</td>
                                                    <td>{item.itemType}</td>
                                                    <td>{item.itemColor}</td>
                                                    <td>{item.itemSize}</td>
                                                    <td className={styles.num}>{formatAmount(item.price)}</td>
                                                    <td className={styles.num}>{formatAmount(item.sellPrice)}</td>
                                                    <td className={styles.num}>{item.totalQuantity}</td>
                                                    <td className={`${styles.num} ${styles.strong}`}>{formatAmount(item.totalAmount)}</td>
                                                    <td>{item.billType}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                        <tfoot>
                                            <tr>
                                                <td colSpan="9">Total</td>
                                                <td className={styles.num}>{sumBy(popupData, 'totalQuantity')}</td>
                                                <td className={styles.num}>{formatAmount(sumBy(popupData, 'totalAmount'))}</td>
                                                <td />
                                            </tr>
                                        </tfoot>
                                    </table>
                                ) : (
                                    <div className={styles.noResults}>
                                        <span className={styles.noResultsIcon} aria-hidden="true">
                                            <FileSearch size={28} />
                                        </span>
                                        <p className={styles.noResultsTitle}>No items found</p>
                                        <p className={styles.noResultsText}>Nothing was sold for this school in this period.</p>
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className={styles.modalFooter}>
                            <span className={styles.modalFooterInfo}>
                                {popupData.length} {popupData.length === 1 ? 'item' : 'items'}
                            </span>
                            <div className={styles.modalFooterActions}>
                                <button type="button" className={`${styles.btn} ${styles.btnGhost}`} onClick={closePopup}>
                                    Close
                                </button>
                                <button
                                    type="button"
                                    className={`${styles.btn} ${styles.btnPrimary}`}
                                    onClick={handleExportToExcel}
                                    disabled={popupData.length === 0}
                                >
                                    <Download size={15} aria-hidden="true" />
                                    Export to Excel
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            <div className={styles.page}>
                <header className={styles.header}>
                    <div className={styles.titleBlock}>
                        <span className={styles.headerIcon} aria-hidden="true">
                            <School size={20} />
                        </span>
                        <h1 className={styles.title}>School Sales Report</h1>
                        <div className={styles.chips}>
                            {startDate && endDate ? (
                                <span className={styles.chip}>
                                    <CalendarRange size={13} aria-hidden="true" />
                                    {formatDate(startDate)} → {formatDate(endDate)}
                                </span>
                            ) : (
                                <span className={styles.chipMuted}>Pick a date range</span>
                            )}
                            {selectedSchool && (
                                <span className={styles.chip}>
                                    <School size={13} aria-hidden="true" />
                                    {selectedSchool}
                                </span>
                            )}
                            {userId && userId !== 'ALL' && (
                                <span className={styles.chip}>
                                    <UserRound size={13} aria-hidden="true" />
                                    {userId}
                                </span>
                            )}
                        </div>
                    </div>

                    <div className={styles.headerActions}>
                        {!loading && hasData && (
                            <button
                                type="button"
                                className={`${styles.btn} ${styles.btnPrimary}`}
                                onClick={handleExportMasterToExcel}
                            >
                                <Download size={15} aria-hidden="true" />
                                Export to Excel
                            </button>
                        )}
                    </div>
                </header>

                {error && (
                    <div className={styles.errorBanner} role="alert">
                        <TriangleAlert size={18} aria-hidden="true" />
                        <span>{error}</span>
                    </div>
                )}

                {/* -------------------- Summary cards -------------------- */}
                <div className={styles.summaryGrid}>
                    {summaryCards.map(({ key, label, value, icon: Icon, primary }) => (
                        <div key={key} className={`${styles.summaryCard} ${primary ? styles.summaryCardPrimary : ''}`}>
                            <span className={styles.summaryIcon} aria-hidden="true">
                                <Icon size={18} />
                            </span>
                            <div className={styles.summaryText}>
                                <span className={styles.summaryLabel}>{label}</span>
                                <span className={styles.summaryValue}>{value}</span>
                            </div>
                        </div>
                    ))}
                </div>

                {/* -------------------- Results card -------------------- */}
                <div className={styles.card}>
                    <div className={styles.toolbar}>
                        <div className={styles.filters}>
                            <label className={styles.inlineField}>
                                <span className={styles.inlineLabel}>From</span>
                                <input
                                    type="date"
                                    value={startDate}
                                    onChange={(e) => setStartDate(e.target.value)}
                                    className={styles.control}
                                />
                            </label>
                            <label className={styles.inlineField}>
                                <span className={styles.inlineLabel}>To</span>
                                <input
                                    type="date"
                                    value={endDate}
                                    onChange={(e) => setEndDate(e.target.value)}
                                    className={styles.control}
                                />
                            </label>

                            <Select
                                className={styles.selectSchool}
                                options={allSchools}
                                ref={selectedSchoolRef}
                                value={allSchools.find(s => s.schoolName === selectedSchool) || null}
                                onChange={handleSelectChange}
                                placeholder="Select a school"
                                styles={selectStyles}
                                menuPortalTarget={document.body}
                                aria-label="School"
                                filterOption={(option, inputValue) =>
                                    option.data.schoolName.toLowerCase().includes(inputValue.toLowerCase()) ||
                                    option.data.schoolCode.toLowerCase().includes(inputValue.toLowerCase())
                                }
                            />

                            <Select
                                className={styles.selectUser}
                                options={userList}
                                value={userList.find(option => option.value === userId)}
                                onChange={(selectedOption) => setUserId(selectedOption.value)}
                                placeholder="Select a user..."
                                styles={selectStyles}
                                menuPortalTarget={document.body}
                                aria-label="User"
                                isSearchable
                            />

                            {selectedSchool && (
                                <button
                                    type="button"
                                    className={`${styles.btn} ${styles.btnGhost}`}
                                    onClick={handleClearSchool}
                                >
                                    <X size={15} aria-hidden="true" />
                                    Clear school
                                </button>
                            )}
                        </div>

                        <div className={styles.toolbarActions}>
                            <button
                                type="button"
                                className={`${styles.btn} ${styles.btnPrimary}`}
                                onClick={handleFetch}
                                disabled={loading}
                            >
                                <Search size={15} aria-hidden="true" />
                                Fetch report
                            </button>
                        </div>
                    </div>

                    <div className={styles.tableWrap}>
                        {loading && (
                            <div className={styles.loadingOverlay}>
                                <span className={styles.spinner} aria-hidden="true" />
                                <span className={styles.loadingText}>Loading report…</span>
                            </div>
                        )}

                        {hasData ? (
                            <table className={styles.table}>
                                <thead>
                                    <tr>
                                        <th>School</th>
                                        <th className={styles.num}>School Sales</th>
                                        <th className={styles.num}>Other Sales</th>
                                        <th className={styles.num}>Total Sales</th>
                                        <th className={styles.alignRight}>Action</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {reportData.map((report, index) => (
                                        <tr key={index}>
                                            <td className={styles.strong}>{report.schoolName}</td>
                                            <td className={styles.num}>{formatAmount(report.schoolSales)}</td>
                                            <td className={styles.num}>{formatAmount(report.otherSales)}</td>
                                            <td className={`${styles.num} ${styles.strong}`}>{formatAmount(report.sales)}</td>
                                            <td className={styles.alignRight}>
                                                <button
                                                    type="button"
                                                    className={`${styles.btn} ${styles.btnGhost} ${styles.btnSm}`}
                                                    onClick={() => handleView(report.schoolName)}
                                                    disabled={loading}
                                                >
                                                    <Eye size={14} aria-hidden="true" />
                                                    View
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                                <tfoot>
                                    <tr>
                                        <td>Total</td>
                                        <td className={styles.num}>{formatAmount(schoolSalesTotal)}</td>
                                        <td className={styles.num}>{formatAmount(otherSalesTotal)}</td>
                                        <td className={styles.num}>{formatAmount(totalSales)}</td>
                                        <td />
                                    </tr>
                                </tfoot>
                            </table>
                        ) : (
                            !loading && (
                                <div className={styles.noResults}>
                                    <span className={styles.noResultsIcon} aria-hidden="true">
                                        <FileSearch size={28} />
                                    </span>
                                    <p className={styles.noResultsTitle}>
                                        {hasFetched ? 'No sales found' : 'Choose a date range'}
                                    </p>
                                    <p className={styles.noResultsText}>
                                        {hasFetched
                                            ? 'Nothing was sold for this selection. Try different dates, school or user.'
                                            : 'Pick From and To dates, optionally a school or user, then press Fetch report.'}
                                    </p>
                                </div>
                            )
                        )}
                    </div>
                </div>
            </div>
        </>
    );
};

export default SalesSchoolReport;